// @ts-check
// Menú de sesión de la barra de navegación superior: pastilla de usuario
// (avatar decorativo con iniciales + nombre/rol reales como texto) + botón
// "Cerrar sesión".
//
// La PASTILLA ES EL ACCESO a "Mi perfil" (DEC-100): es un enlace, no un
// adorno. Antes había tres cosas en fila -- la pastilla decorativa y, al
// lado, "Mi perfil" y "Mis premios" --, y el usuario pidió que se entre por
// el elemento que muestra "usuario · rol" y no por otro botón contiguo. Los
// premios dejaron de tener URL propia y viven dentro del perfil, así que ya
// no hay nada que enlazar aparte.
//
// Componente PURO usado por los dos montadores del top-nav que quedan
// (`pages/shell-nav.js`, que sirve a las siete páginas con barra completa, y
// `pages/auth-shell-nav.js`): unifica el markup que antes vivía triplicado
// en las cabeceras de sesión de cada página de editor
// (`crearCabeceraSesion`/`crearTopbar`, ahora sin pastilla). Se inserta en el
// `slotSesion` de `components/top-nav.js`.
//
// Igual que el resto de `components/**` (docs/03-frontend.md §4 "components no
// llaman fetch; services no tocan el DOM"): NO importa `services/**`,
// `state/**` ni `features/**`, no llama a `fetch` ni a `window.location`. La
// orquestación real del logout (llamar a `cerrarSesion()` del store + redirigir)
// la construye la PÁGINA que monta este componente y se recibe aquí como un
// callback OPACO `onCerrarSesion` -- este componente solo lo invoca al pulsar
// el botón, absorbiendo cualquier fallo y restaurando el botón (misma red de
// seguridad defensiva que la antigua `crearCabeceraSesion`).
//
// Ningún dato de sesión sensible (id, email, token, csrf) se escribe en ningún
// atributo/dataset del DOM: solo `nombreVisible`/`rol`, como texto visible --
// exactamente lo estrictamente necesario para mostrar la identidad.
import { createSafeElement, setSafeAttribute } from '../security/safe-dom.js';
import './base/chip-usuario.css';
import './button.css';
import './session-menu.css';

/**
 * Extrae hasta dos iniciales en mayúscula de un nombre visible, para el avatar
 * puramente decorativo. Mismo patrón que
 * `pseudocode-editor-page.js#obtenerIniciales`/`editor-controller.js#obtenerIniciales`
 * (leídos como referencia). Nunca es la única fuente de la identidad en el DOM:
 * el texto real (nombre + rol) vive siempre en un nodo de texto aparte.
 * @param {string} nombreVisible
 * @returns {string}
 */
export function obtenerIniciales(nombreVisible) {
  const partes = nombreVisible.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '';
  const primera = partes[0]?.[0] ?? '';
  const segunda = partes.length > 1 ? (partes[1]?.[0] ?? '') : '';
  return (primera + segunda).toUpperCase();
}

/**
 * @typedef {Object} OpcionesMenuSesion
 * @property {{ nombreVisible: string, rol: string }} usuario - Datos visibles del usuario autenticado.
 * @property {() => void | Promise<void>} onCerrarSesion - Callback OPACO que orquesta el logout (construido por la página que monta el componente). Se invoca al pulsar "Cerrar sesión".
 */

/**
 * Crea el menú de sesión (pastilla + botón "Cerrar sesión").
 * @param {OpcionesMenuSesion} opciones
 * @returns {{ elemento: HTMLElement, destruir: () => void }}
 */
export function crearMenuSesion(opciones) {
  const { usuario, onCerrarSesion } = opciones;

  const contenedor = createSafeElement('div', { className: 'session-menu' });

  // `<a>` y no `<button>`: navega a otra página del MPA, así que tiene que
  // comportarse como un enlace (abrir en pestaña nueva, menú contextual...).
  const pill = createSafeElement('a', { className: 'session-menu-pill ui-chip-usuario' });
  setSafeAttribute(pill, 'href', 'perfil.html');

  const avatar = createSafeElement('span', {
    className: 'session-menu-avatar ui-chip-usuario-avatar',
    text: obtenerIniciales(usuario.nombreVisible),
  });
  avatar.setAttribute('aria-hidden', 'true');

  const etiquetaVisible = usuario.rol
    ? `${usuario.nombreVisible} · ${usuario.rol}`
    : usuario.nombreVisible;
  const texto = createSafeElement('span', {
    className: 'session-menu-text ui-chip-usuario-texto',
    text: etiquetaVisible,
  });

  pill.append(avatar, texto);

  // El texto visible identifica a la persona, pero no dice A DÓNDE lleva el
  // enlace: "Alberto · estudiante" no es un destino. El nombre accesible
  // EMPIEZA por el texto visible y añade el destino, que es lo que exige
  // WCAG 2.2 SC 2.5.3 (Label in Name) -- si el `aria-label` no contuviera la
  // etiqueta visible, quien usa control por voz no podría activarlo diciendo
  // lo que ve.
  setSafeAttribute(pill, 'aria-label', `${etiquetaVisible}. Mi perfil y mis premios`);

  const boton = createSafeElement('button', {
    className: 'session-menu-logout ui-button ui-button-secundario',
    text: 'Cerrar sesión',
  });
  boton.setAttribute('type', 'button');

  let procesando = false;
  const manejarClick = () => {
    if (procesando) return;
    procesando = true;
    boton.setAttribute('disabled', '');
    boton.textContent = 'Cerrando sesión…';
    // `onCerrarSesion` (construido por la página) ya absorbe cualquier fallo
    // de red y SIEMPRE redirige: este `catch`/`finally` es una red de
    // seguridad puramente defensiva para no dejar una promesa rechazada sin
    // manejar ni el botón deshabilitado para siempre si esa garantía cambiase.
    Promise.resolve(onCerrarSesion())
      .catch(() => {})
      .finally(() => {
        procesando = false;
        boton.removeAttribute('disabled');
        boton.textContent = 'Cerrar sesión';
      });
  };
  boton.addEventListener('click', manejarClick);

  // Orden de tabulación: "quién soy (y mi perfil) -> salir".
  contenedor.append(pill, boton);

  return {
    elemento: contenedor,
    destruir: () => {
      boton.removeEventListener('click', manejarClick);
      contenedor.remove();
    },
  };
}
